import { differenceInDays, format } from 'date-fns';
import { Link } from 'react-router';
import { Center, Column, Row, Tooltip, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { isPartialShift } from '~/commonComponents/CommonFunctions';
import { UserSubProfession } from '~/enums/UserProfession';
import { useAppSelector, useUserSelector } from '~/hooks';
import { SolidInformationCircleIcon } from '@tempmee/react-icons';
import { IJob, IMultipleCounters } from '~/models';
import { Colors } from '~/themes/colors';
import { getProfessionalPayChargeDate } from '~/utils/chargeTimeline';
import { EducationUtils } from '~/utils/EducationUtils';
import { PartialShift } from '../../CandidateList/components/PartialShift';
import { renderOfferExpirationDateTime } from '../../counterJobs/RenderFunc';
import { isEmployeeOptionShift } from '../../WorkerClassification/utils';
import { HighlightedLines } from './HighlightedLines';
import { useMemo } from 'react';

const ConvenienceFeeTooltip =
  'GoTu charges a 3.5% convenience fee on Credit Card transactions. If you do not wish to be charged the convenience fee, please update your billing to E-check/ACH.';

const OvertimeTooltip =
  'Overtime pay (1.5x rate) is required for hours that exceed the state’s daily and/or weekly regular working hour limits.';

interface IShiftDetailsProps {
  currentCounter: IMultipleCounters;
  hasBorderStyle?: boolean;
  isLiteVersion?: boolean;
  job?: Partial<IJob> | null;
}

export const ShiftDetails = ({
  currentCounter,
  hasBorderStyle = true,
  isLiteVersion = false,
  job: queryJob,
}: IShiftDetailsProps) => {
  const currentUser = useUserSelector();
  const { job: reduxJob } = useAppSelector((state) => state.job);
  const job = queryJob ?? reduxJob;

  const {
    isOnsiteInterview,
    job_datetime: jobDateTime,
    local_date: localDate,
    local_end_time: localEndTime,
    local_start_time: localStartTime,
    offer_owner: offerOwner,
    rate,
    specialty,
  } = job || {};
  const { payment } = currentCounter || {};

  const chargeDate = jobDateTime ? format(getProfessionalPayChargeDate(jobDateTime), 'MM/dd/yyyy') : '';
  const startDiff = jobDateTime ? differenceInDays(new Date(jobDateTime), new Date()) : 0;

  const totalAmount = payment?.totalAmount;
  const tempMeeFee = payment?.tempmeeFee;
  const tempMeeFeePercent = payment?.tempmeeFeeRatio
    ? (payment?.tempmeeFeeRatio * 100).toFixed(1)
    : 0;
  const convenienceFee = currentCounter?.payment?.convenienceFee ?? 0;
  const convenienceFeePercent = currentCounter?.payment?.convenienceFeeRatio
    ? (currentCounter?.payment?.convenienceFeeRatio * 100).toFixed(1)
    : 0;

  const hasConvenienceFee = convenienceFeePercent !== 0 && convenienceFee;
  const hasOvertimeHours = job?.regularAndOvertimeHours?.overtime > 0;
  const hasRegularHours = job?.regularAndOvertimeHours?.regular > 0;
  const hasTotalOvertime = job?.totalOvertime > 0;
  const hasTotalRegular = job?.totalRegular > 0;
  const isCounterHours = currentCounter ? isPartialShift(currentCounter) : null;
  const isCounterRate = currentCounter?.candidateRate !== rate;

  const fmtProfessionName = useMemo(() => {
    if (job?.subProfession === UserSubProfession.EFDA) {
      return 'Expanded Functions DA';
    }

    return job?.professionName;
  }, [job?.professionName, job?.subProfession]);

  return (
    <Column
      style={hasBorderStyle ? { border: `1px solid ${colors.neutral[150]}` } : undefined}
    >
      {!isLiteVersion && (
        <Column
          align="center"
          py={4}
          style={{
            backgroundColor: isOnsiteInterview
              ? Colors.jobs_interview_background
              : Colors.jobs_shift_background,
          }}
        >
          <Typography as="span" bold color={colors.primary[500]} size="heading_4" textAlign="center">
            {isOnsiteInterview ? 'Interview Details' : 'Shift Details'}
          </Typography>
        </Column>
      )}
      <Column {...(hasBorderStyle ? { px: 8, pt: 5, pb: 8 } : {})}>
        {!isLiteVersion && (
          <>
            {isCounterHours && (
              <Center data-cy="counter-hours-indicator">
                <PartialShift />
              </Center>
            )}
            {renderOfferExpirationDateTime(currentCounter?.expiration, currentUser?.time_zone)}
          </>
        )}

        <Column gap={2}>
          <Row align="center" justify="between">
            <Typography as="span" bold>Office:</Typography>
            <Typography as="span" numberOfLines={1} style={{ maxWidth: 200 }}>
              {offerOwner?.office_name}
            </Typography>
          </Row>
          {isOnsiteInterview && (
            <Row align="center" justify="between">
              <Typography as="span" bold color={colors.neutral[600]}>Job Title:</Typography>
              {isLiteVersion ? (
                <Column align="center" justify="end">
                  <Typography
                    as="span"
                    color={colors.neutral[500]}
                    numberOfLines={1}
                    style={{ maxWidth: 200 }}
                  >
                    {job?.postingTitle}
                  </Typography>
                </Column>
              ) : (
                <Column align="center" justify="end">
                  <Link to={`/permanent-jobs/job-overview/${job?.postingId}`}>
                    <Typography as="span" color={colors.neutral[500]} style={{ textDecoration: 'underline' }}>
                      {job?.postingTitle}
                    </Typography>
                  </Link>
                </Column>
              )}
            </Row>
          )}
          <Row align="center" justify="between">
            <Typography as="span" bold>Date:</Typography>
            <Typography as="span">{localDate}</Typography>
          </Row>
          <Row align="center" justify="between">
            <Typography as="span" bold>Professional Type:</Typography>
            <Typography as="span">{fmtProfessionName}</Typography>
          </Row>
          <Row align="center" justify="between">
            <Typography as="span" bold>Preferred Specialty:</Typography>
            <Typography as="span" textAlign="right" style={{ textTransform: 'capitalize' }}>
              {EducationUtils.removeSpecialtyPrefix(specialty) || 'General'}
            </Typography>
          </Row>
          <HighlightedLines highlight={isCounterHours}>
            <Row data-cy="detail-row-original-hours" justify="between">
              <Row gap={2}>
                <Typography as="span" bold>Hours:</Typography>
                <Center
                  height="min-content"
                  px={1}
                  style={{ backgroundColor: colors.neutral[300], borderRadius: 50 }}
                >
                  <Typography as="span" color={colors.shades[0]} size="label">
                    EST
                  </Typography>
                </Center>
              </Row>
              <Typography
                as="span"
                data-cy="original-hours-value"
                style={{ textDecoration: isCounterHours ? 'line-through' : 'none' }}
              >
                {`${localStartTime} - ${localEndTime}`}
              </Typography>
            </Row>
            {isCounterHours && (
              <Row data-cy="detail-row-hours" justify="between">
                <Typography as="span" bold>*Counter Offer (hours):</Typography>
                <Typography as="span" bold data-cy="hours-value" style={{ textTransform: 'lowercase' }}>
                  {`${currentCounter?.local_begin} - ${currentCounter?.local_end}`}
                </Typography>
              </Row>
            )}
          </HighlightedLines>
          <HighlightedLines highlight={isCounterRate}>
            <Row justify="between">
              <Typography as="span" bold>Hourly Rate:</Typography>
              <Typography
                as="span"
                data-cy="original-rate"
                style={{
                  textDecoration:
                    job?.rate !== currentCounter?.candidateRate ? 'line-through' : 'none',
                }}
              >
                ${rate?.toFixed(2) || '0.00'} /hr
              </Typography>
            </Row>
            {isCounterRate && (
              <Row justify="between">
                <Typography as="span" bold>*Counter Offer (rate):</Typography>
                <Typography as="span" bold data-cy="counter-rate">
                  ${currentCounter?.candidateRate?.toFixed(2)} /hr
                </Typography>
              </Row>
            )}
          </HighlightedLines>
          <Row justify="between">
            <Typography as="span" bold>Meal Break:</Typography>
            <Typography as="span">
              {job?.lunch_final_break_time} min {job?.is_lunch_break_paid ? ' (Paid)' : ' (Unpaid)'}
            </Typography>
          </Row>
          <HighlightedLines highlight={hasOvertimeHours}>
            {hasRegularHours && hasTotalRegular && (
              <Row data-cy="detail-row-regular-pay" justify="between">
                <Typography as="span" bold>
                  Regular Rate Pay (
                  <span data-cy="working-hours-value">
                    {job?.regularAndOvertimeHours?.regular?.toFixed(2)}
                  </span>{' '}
                  hrs):
                </Typography>
                <Typography as="span" data-cy="regular-pay-value">${job?.totalRegular?.toFixed(2)}</Typography>
              </Row>
            )}
            {hasOvertimeHours && hasTotalOvertime && (
              <Row justify="between">
                <Row align="center" gap={1}>
                  <Typography as="span" bold>
                    *Overtime Pay ({job?.regularAndOvertimeHours?.overtime?.toFixed(2)} hrs):
                  </Typography>
                  <Tooltip
                    content={OvertimeTooltip}
                    placement="bottom"
                  >
                    <SolidInformationCircleIcon color={colors.neutral[300]} />
                  </Tooltip>
                </Row>
                <Typography as="span" bold>${job?.totalOvertime?.toFixed(2)}</Typography>
              </Row>
            )}
          </HighlightedLines>
          <Row data-cy="detail-row-service-fee" justify="between">
            <Typography as="span" bold>Service Fee{tempMeeFeePercent !== 0 && ` (${tempMeeFeePercent}%)`}:</Typography>
            <Typography as="span" data-cy="service-fee">${tempMeeFee?.toFixed(2)}</Typography>
          </Row>
          {payment?.peoFee !== 0 && isEmployeeOptionShift(job) && (
            <Row justify="between">
              <Typography as="span" bold>Employee Fee:</Typography>
              <Typography as="span">${payment?.peoFee?.toFixed(2)}</Typography>
            </Row>
          )}
          {hasConvenienceFee && (
            <Row data-cy="detail-row-convenience-fee" justify="between">
              <Row align="center" gap={1}>
                <Typography as="span" bold>Convenience Fee {`(${convenienceFeePercent || '0'}%)`}:</Typography>
                <Tooltip
                  content={ConvenienceFeeTooltip}
                  placement="bottom"
                >
                  <SolidInformationCircleIcon color={colors.neutral[300]} />
                </Tooltip>
              </Row>
              <Typography as="span" data-cy="convenience-fee">${convenienceFee?.toFixed(2) || '0'}</Typography>
            </Row>
          )}
          {job?.multi_day_booking && startDiff > 28 && !offerOwner?.is_pre_approved && (
            <Row justify="between">
              <Typography as="span" bold>Professional Pay Charge Date:</Typography>
              <Typography as="span">{chargeDate}</Typography>
            </Row>
          )}
          {job?.counterCover && (
            <Row data-cy="detail-row-counter-cover" justify="between">
              <Typography as="span" bold data-cy="counter-cover-label">
                Gotu Counter Cover:
              </Typography>
              <Typography as="span" data-cy="counter-cover-value">-$ {job?.counterCover?.amount?.toFixed(2)}</Typography>
            </Row>
          )}
          <Row align="center" justify="between">
            <Typography as="span" bold color={colors.success[500]} style={{ alignItems: 'center', columnGap: 5 }}>
              Total Pay:
            </Typography>
            <Typography as="span" bold data-cy="total-pay">
              ${totalAmount?.toFixed(2)}
            </Typography>
          </Row>
        </Column>
      </Column>
    </Column>
  );
};
